import React, { MouseEvent, useEffect, useRef } from 'react';
import { ReactSVG } from 'react-svg';
import classNames from 'classnames';
import interact from 'interactjs';
import { DocumentFieldShape, DocumentFieldTypes } from 'Interfaces/DocumentFields';
import { capitalize } from 'lodash-es';

export interface FieldBarItemProps {
  selectedFieldType?: string;
  fieldShape: DocumentFieldShape;
  disabled?: boolean;
  handleSelectField: (fieldType: DocumentFieldTypes) => void;
  onCursorMove: (event, fieldType: DocumentFieldTypes) => void;
  onClick: (
    event: MouseEvent<HTMLLIElement>,
    type: DocumentFieldTypes,
    renderShape?: boolean,
  ) => void;
}

function FieldBarItem({
  selectedFieldType,
  fieldShape: { label, type, iconType, icon },
  disabled = false,
  onCursorMove,
  onClick,
}: FieldBarItemProps) {
  const fieldTypeItemRef = useRef<HTMLLIElement>(null);
  const isPointerCapturedRef = useRef(false);

  useEffect(() => {
    if (!fieldTypeItemRef.current || disabled) return;

    const interactableListItem = interact(fieldTypeItemRef.current)
      .on('down', event => {
        isPointerCapturedRef.current = true;
        onClick(event, type);
      })
      .on('move', event => {
        isPointerCapturedRef.current && onCursorMove(event, type);
        isPointerCapturedRef.current = false;
      });

    return () => interactableListItem.unset();
  }, [fieldTypeItemRef, type, disabled, onCursorMove, onClick]);

  return (
    <li
      ref={fieldTypeItemRef}
      className={classNames('interactModal__fieldBar-fieldItem', {
        'interactModal__fieldBar-fieldItem--active': type === selectedFieldType,
        'interactModal__fieldBar-fieldItem--stroke': iconType === 'stroke',
        'interactModal__fieldBar-fieldItem--fill': iconType === 'fill',
        'interactModal__fieldBar-fieldItem--disabled': disabled,
      })}
      onClick={event => !disabled && onClick(event, type, true)}
    >
      <ReactSVG src={icon} className="interactModal__fieldBar-fieldItem-icon" />
      {capitalize(label)}
    </li>
  );
}

export default FieldBarItem;
